// Topic areas for exam practice: every section shows a short list of TOPIC AREAS, each holding clear TOPICS
// ("Fractions and decimals" → "Equivalent fractions", …), instead of one flat list of raw generator topic names.
//
// The banks keep their original `topic` fields. src/data/examTopics.json maps them for display and filtering:
//   naplan: { <subject>: { <raw topic>: { area, topic } } }   — NAPLAN topics are shared by many questions
//   retag:  { <question id>: { area, topic } }                — other exams' questions had ~one topic each
// applyTopic() gives a question its clean `topic` and its `area`; anything unmapped keeps a tidied name under
// "More practice", so a new bank never disappears from the list.

import MAP from '../data/examTopics.json';

export const OTHER_AREA = 'More practice';
const AREA_PREFIX = 'area:';

/** "Author'S Purpose" → "Author's purpose", "2d Shapes" → "2D shapes", "Addition Subtraction" stays readable. */
export function tidyTopic(raw) {
  const s = String(raw || '').trim().replace(/_/g, ' ').replace(/\s+/g, ' ');
  if (!s) return 'General';
  const lower = s.toLowerCase();
  return (lower.charAt(0).toUpperCase() + lower.slice(1)).replace(/\b([23])d\b/g, '$1D');
}

export function applyTopic(q, examProg) {
  if (!q) return q;
  const m = examProg === 'naplan'
    ? (MAP.naplan?.[q.subject === 'language_conventions' ? 'language' : q.subject] || {})[q.topic]
    : MAP.retag?.[q.id];
  // expansion banks (scripts/exam-expand/) already carry clean area/topic names: keep them, map only when absent
  if (!m && q.area && q.topic) return q;
  const topic = m?.topic || tidyTopic(q.topic);
  const area = m?.area || OTHER_AREA;
  if (topic === q.topic && area === q.area) return q;
  return { ...q, rawTopic: q.rawTopic || q.topic, topic, area };
}

/** A topic filter is either a topic name or a whole area ("area:<name>"). */
export const areaFilter = (area) => AREA_PREFIX + area;
export function matchesFilter(q, filter) {
  if (!filter) return true;
  return filter.startsWith(AREA_PREFIX) ? q.area === filter.slice(AREA_PREFIX.length) : q.topic === filter;
}
export function filterLabel(filter) {
  return filter && filter.startsWith(AREA_PREFIX) ? filter.slice(AREA_PREFIX.length) : filter;
}

/**
 * Group a pool into areas → topics with counts. Topics with fewer than `min` questions are left out (too few for a
 * session) and areas with no topics left are dropped. Areas keep the map's order; "More practice" goes last.
 */
export function groupTopics(pool, min = 5) {
  const areas = new Map();
  for (const q of pool) {
    const a = q.area || OTHER_AREA;
    if (!areas.has(a)) areas.set(a, new Map());
    const t = areas.get(a);
    t.set(q.topic, (t.get(q.topic) || 0) + 1);
  }
  const out = [];
  for (const [area, topics] of areas) {
    const list = [...topics].filter(([, n]) => n >= min).map(([name, count]) => ({ name, count })).sort((x, y) => x.name.localeCompare(y.name));
    if (list.length) out.push({ area, count: list.reduce((s, t) => s + t.count, 0), topics: list });
  }
  return out.sort((x, y) => (x.area === OTHER_AREA) - (y.area === OTHER_AREA) || x.area.localeCompare(y.area));
}
